import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { LeadActivityService } from 'src/app/private/services/lead-activity.service';
import { LeadService } from 'src/app/private/services/lead.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import {
  buildLeadOverviewSections,
  LeadOverviewSection,
} from 'src/app/utils/lead-profile-overview-sections.util';
import {
  isSelectLeadField,
  LEAD_SOURCE_OPTIONS,
  LEAD_TYPE_OPTIONS,
  normalizeFieldOptions,
} from 'src/app/utils/lead-field-options.util';
import {
  compareSelectStrings,
  commitResolvedRootValues,
  LEAD_ROOT_SYNC_KEYS,
  mergeLeadUpdateResponse,
  normalizeLeadEditModel,
  resolveFieldDisplayValue,
  resolveRootDisplayValue,
  selectOptionsWithCurrent,
  syncRootAndCustomFields,
  writeFieldValue,
} from 'src/app/utils/lead-profile-field.util';
import { getEffectiveLeadFieldMinDateIso } from 'src/app/utils/lead-field-date.util';
import { applyCrmSourcePipeline } from 'src/app/utils/crm-lead-pipeline.util';

@Component({
  selector: 'app-lead-profile-overview',
  templateUrl: './lead-profile-overview.component.html',
  styleUrls: ['./lead-profile-overview.component.scss'],
})
export class LeadProfileOverviewComponent implements OnChanges {
  @Input() editFieldValues: any;
  @Input() allLeadFields: any[] = [];
  @Input() pipelineData: any[] = [];
  @Input() leadStatusList: { value: string; label: string }[] = [];
  @Output() saved = new EventEmitter<void>();
  @Output() pipelineChange = new EventEmitter<any>();

  sections: LeadOverviewSection[] = [];
  expanded: Record<string, boolean> = {};
  btnLoading = false;
  isSubmitted = false;
  isFixedColumn = false;
  /** Stable bindings for fixed root inputs (avoids method-based ngModel on selects). */
  formRoot: Record<string, string> = {};
  readonly compareSelectOption = compareSelectStrings;

  constructor(
    private api: LeadService,
    private leadActivity: LeadActivityService,
    private toast: ToastrService,
    private localStorage: LocalStorageService
  ) {
    this.isFixedColumn = this.localStorage.getIsFixedCol();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const leadChanged = !!changes['editFieldValues']?.currentValue;
    const metaChanged =
      !!changes['allLeadFields'] ||
      !!changes['leadStatusList'] ||
      !!changes['pipelineData'];

    if (leadChanged || metaChanged) {
      if (this.editFieldValues) {
        const previousPipelineId = this.editFieldValues.pipeline_id;
        const appliedCrmPipeline = applyCrmSourcePipeline(
          this.editFieldValues,
          this.pipelineData
        );
        if (
          appliedCrmPipeline &&
          String(previousPipelineId ?? '') !== String(this.editFieldValues.pipeline_id ?? '')
        ) {
          this.pipelineChange.emit(this.editFieldValues.pipeline_id);
        }
        normalizeLeadEditModel(this.editFieldValues, this.allLeadFields);
        this.patchFormRoot();
        if (this.editFieldValues.pipeline_id != null && this.editFieldValues.pipeline_id !== '') {
          this.formRoot['pipeline'] = String(this.editFieldValues.pipeline_id);
        }
      }
    }
    if (leadChanged || !!changes['allLeadFields']) {
      this.rebuildSections();
    }
  }

  private patchFormRoot(): void {
    if (!this.editFieldValues) {
      return;
    }
    commitResolvedRootValues(this.editFieldValues, this.allLeadFields);
    for (const key of LEAD_ROOT_SYNC_KEYS) {
      this.formRoot[key] = resolveRootDisplayValue(
        this.editFieldValues,
        key,
        this.allLeadFields
      );
    }
  }

  private rebuildSections(): void {
    this.sections = buildLeadOverviewSections(this.allLeadFields, {
      includeFixedContact: this.isFixedColumn,
    });
    this.sections.forEach((s, idx) => {
      if (this.expanded[s.id] === undefined) {
        this.expanded[s.id] = idx < 2 || !!s.expandedByDefault;
      }
    });
  }

  toggleSection(id: string): void {
    this.expanded[id] = !this.expanded[id];
  }

  isExpanded(id: string): boolean {
    return !!this.expanded[id];
  }

  isRequired(item: any): boolean {
    return item?.required === true || item?.required === 'true';
  }

  isSelectField(item: any): boolean {
    return isSelectLeadField(item);
  }

  fieldSelectOptions(item: any): string[] {
    const opts = normalizeFieldOptions(item?.data);
    return selectOptionsWithCurrent(opts, resolveFieldDisplayValue(this.editFieldValues, item));
  }

  getFixedFieldOptions(ff: { optionsKey?: string; key?: string }): { value: string; label: string }[] {
    const key = ff.optionsKey || ff.key || '';
    const rootKey = ff.key || key;
    const current = this.formRoot[rootKey] ?? this.editFieldValues?.[rootKey];
    switch (key) {
      case 'lead_source':
        return selectOptionsWithCurrent(LEAD_SOURCE_OPTIONS, current).map((v) => ({
          value: v,
          label: v,
        }));
      case 'lead_type':
        return selectOptionsWithCurrent(LEAD_TYPE_OPTIONS, current).map((v) => ({
          value: v,
          label: v,
        }));
      case 'lead_status': {
        const base = (this.leadStatusList || []).map((s) => s.value);
        return selectOptionsWithCurrent(base, current).map((v) => {
          const match = this.leadStatusList?.find((s) => s.value === v);
          return { value: v, label: match?.label || v };
        });
      }
      case 'pipeline':
        return (this.pipelineData || []).map((p: any) => ({
          value: String(p.pipeline_id),
          label: p.pipeline_name || String(p.pipeline_id),
        }));
      default:
        return [];
    }
  }

  fieldValue(item: any): any {
    return resolveFieldDisplayValue(this.editFieldValues, item);
  }

  getLeadFieldMinDateIso(item: { 'min-length'?: string }): string {
    return getEffectiveLeadFieldMinDateIso(item?.['min-length']);
  }

  setFieldValue(item: any, value: any): void {
    writeFieldValue(this.editFieldValues, item.field_name, value);
    syncRootAndCustomFields(this.editFieldValues);
  }

  fixedRootValue(key: string): string {
    if (this.formRoot[key] != null && this.formRoot[key] !== '') {
      return this.formRoot[key];
    }
    const v = this.editFieldValues?.[key];
    return v == null ? '' : String(v);
  }

  determineLeadType(status: string): string | null {
    if (!status || !this.pipelineData || !this.editFieldValues?.pipeline_id) return null;
    const pipe = this.pipelineData.find((p: any) => String(p.pipeline_id) === String(this.editFieldValues.pipeline_id));
    if (!pipe || !pipe.stages) return null;
    const stage = pipe.stages.find((s: any) => (s.stage_name || '').trim().toLowerCase() === status.trim().toLowerCase());
    if (!stage || stage.probability == null) return null;
    const prob = Number(stage.probability);
    if (isNaN(prob)) return null;
    if (prob < 30) return 'Cold Lead';
    if (prob < 70) return 'Warm Lead';
    return 'Hot Lead';
  }

  onFixedRootChange(key: string, value: string): void {
    const v = value == null ? '' : String(value).trim();
    this.formRoot[key] = v;
    writeFieldValue(this.editFieldValues, key, v);

    if (key === 'lead_source' && applyCrmSourcePipeline(this.editFieldValues, this.pipelineData)) {
      this.formRoot['pipeline'] = String(this.editFieldValues.pipeline_id || '');
      this.pipelineChange.emit(this.editFieldValues.pipeline_id);
    }

    if (key === 'pipeline') {
      this.editFieldValues.pipeline_id = Number(v);
      this.pipelineChange.emit(this.editFieldValues.pipeline_id);
    }

    if (key === 'lead_status' || key === 'pipeline') {
      const autoType = this.determineLeadType(this.editFieldValues.lead_status);
      if (autoType) {
        this.formRoot['lead_type'] = autoType;
        writeFieldValue(this.editFieldValues, 'lead_type', autoType);
      }
    }

    syncRootAndCustomFields(this.editFieldValues);
  }

  subFieldValue(item: any, subIndex: number, sub: any): unknown {
    const key = item.field_name + '|subf';
    const cellKey = sub.field_name + '|' + subIndex;
    return this.editFieldValues?.fields?.[key]?.[subIndex]?.[cellKey] ?? '';
  }

  setSubField(item: any, subIndex: number, sub: any, value: any): void {
    const key = item.field_name + '|subf';
    if (!this.editFieldValues.fields[key]) {
      this.editFieldValues.fields[key] = [];
    }
    if (!this.editFieldValues.fields[key][subIndex]) {
      this.editFieldValues.fields[key][subIndex] = {};
    }
    const cellKey = sub.field_name + '|' + subIndex;
    this.editFieldValues.fields[key][subIndex][cellKey] = value;
    if (sub.type === 'date') {
      this.editFieldValues.fields[key][subIndex][cellKey] = value;
    }
  }

  updateDetails(): void {
    if (!this.editFieldValues?.lead_id) {
      return;
    }
    if (this.isFixedColumn && !this.editFieldValues.contact_name?.trim()) {
      this.toast.error('Contact name is required');
      return;
    }

    this.isSubmitted = true;
    this.btnLoading = true;
    this.editFieldValues.client_id = this.localStorage.getClientId();
    this.editFieldValues.center_id = this.localStorage.getCenterId();

    syncRootAndCustomFields(this.editFieldValues);

    this.api.updateLead(this.editFieldValues).subscribe({
      next: (res: any) => {
        mergeLeadUpdateResponse(this.editFieldValues, res);
        normalizeLeadEditModel(this.editFieldValues, this.allLeadFields);
        this.patchFormRoot();
        this.toast.success('Lead updated successfully');
        this.btnLoading = false;
        this.isSubmitted = false;
        this.leadActivity.recordLeadDetailsUpdated(this.editFieldValues.lead_id).subscribe({
          next: () => this.saved.emit(),
          error: () => this.saved.emit(),
        });
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'Update failed');
        this.btnLoading = false;
        this.isSubmitted = false;
      },
    });
  }
}
